import React, { useState, useRef, useEffect } from 'react';
import { usePageEditor } from './PageEditorContext';
import { Edit3, Check, X, EyeOff, RotateCcw } from 'lucide-react';

export const HIDDEN_SENTINEL = '__HIDDEN__';

const EditableText = ({ 
  sectionId, 
  field, 
  defaultValue = '', 
  as: Component = 'p',
  className = '',
  placeholder = 'Click to edit...',
  multiline = false,
  ...props 
}) => {
  const { isEditMode, getContent, updateContent } = usePageEditor();
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState('');
  const inputRef = useRef(null);
  
  const content = getContent(sectionId, field, defaultValue);
  
  useEffect(() => {
    setLocalValue(content);
  }, [content]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Place cursor at end
      if (inputRef.current.setSelectionRange) {
        const len = inputRef.current.value.length;
        inputRef.current.setSelectionRange(len, len);
      }
    }
  }, [isEditing]);

  const handleClick = (e) => {
    if (isEditMode) {
      e.preventDefault();
      e.stopPropagation();
      if (!isEditing) setIsEditing(true);
    }
  };

  const handleSave = () => {
    updateContent(sectionId, field, localValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalValue(content);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // Not in edit mode - render normally (hidden blocks render nothing)
  if (!isEditMode) {
    if (content === HIDDEN_SENTINEL) return null;
    return <Component className={className} {...props}>{content || defaultValue}</Component>;
  }

  // In edit mode: hidden block shows a restore placeholder
  if (content === HIDDEN_SENTINEL) {
    return (
      <Component
        className={`${className} cursor-pointer border-2 border-dashed border-gray-300 rounded-lg px-3 py-1.5 text-gray-400 text-sm italic hover:border-[#E91E8C]/50 hover:text-[#E91E8C] transition-colors inline-flex items-center gap-2`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateContent(sectionId, field, defaultValue); }}
        data-testid="hidden-text-placeholder"
        {...props}
      >
        <RotateCcw className="w-3.5 h-3.5" />
        Hidden text — click to restore
      </Component>
    );
  }

  // In edit mode but not actively editing this field
  if (!isEditing) {
    return (
      <Component 
        className={`${className} cursor-pointer relative group ring-2 ring-transparent hover:ring-[#E91E8C]/50 hover:bg-[#E91E8C]/5 rounded transition-all`}
        onClick={handleClick}
        {...props}
      >
        {content || <span className="text-gray-400 italic">{placeholder}</span>}
        <span className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="bg-[#E91E8C] text-white p-1 rounded-full shadow-lg">
            <Edit3 className="w-3 h-3" />
          </span>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); updateContent(sectionId, field, HIDDEN_SENTINEL); }}
            className="bg-gray-700 hover:bg-red-600 text-white p-1 rounded-full shadow-lg transition-colors"
            title="Hide this text (removes it from the page — you can restore it while in edit mode)"
            data-testid="hide-text-btn"
          >
            <EyeOff className="w-3 h-3" />
          </button>
        </span>
        <span className="absolute -top-9 left-0 bg-gray-900 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-xl z-50">
          Click to edit — or use the eye icon to hide
        </span>
      </Component>
    );
  }

  // Actively editing
  return (
    <div className="relative">
      {multiline ? (
        <textarea
          ref={inputRef}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          className={`${className} w-full bg-white border-2 border-[#E91E8C] rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/50 resize-y min-h-[100px]`}
          placeholder={placeholder}
        />
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyDown} 
          onBlur={handleSave}
          className={`${className} w-full bg-white border-2 border-[#E91E8C] rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/50`}
          placeholder={placeholder}
        />
      )}
      <div className="absolute -top-2 -right-2 flex gap-1">
        <button
          onClick={handleSave}
          className="bg-green-500 text-white p-1 rounded-full shadow-lg hover:bg-green-600 transition-colors"
          title="Save"
        >
          <Check className="w-3 h-3" />
        </button>
        <button
          onClick={handleCancel}
          className="bg-gray-500 text-white p-1 rounded-full shadow-lg hover:bg-gray-600 transition-colors"
          title="Cancel"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export default EditableText;
